import React, { useState } from 'react';
import {
    COUNTRY_COUNT,
    LEGAL_SOURCE_TRUE_ID,
} from '../../../constants';
import { useAppState } from '../../../hooks/use-app-state.hook';
import {
    getTaxonomyName,
    toCountryProfileList
} from '../../../utils';
import Content from '../../layout/content/content.component';
import Select from '../../layout/form/select.component';
import QuotaTypeChart from '../quota-type-chart/quota-type-chart.component';
import { Cell, Grid } from '../../layout/grid/grid.component';
import Icon from '../../layout/icon/icon.component';
import Spacer from '../../layout/spacer/spacer.component';
import {
    globalStatisticsClassName,
    globalStatisticsDropdownTitleClassName,
    globalStatisticsDropdownIconClassName,
    globalStatisticsDataNameClassName,
    globalStatisticsListClassName,
    globalStatisticsListNumberClassName,
    globalStatisticsListDescriptionClassName,
    globalStatisticsNoteClassName,
    globalStatisticsGridWrapperNameClassName,
    globalStatisticsBarsClassName,
    globalStatisticsSubtitleClassName,
    globalStatisticsThemeGreenClassName,
    globalStatisticsThemeBlueClassName
} from './global-statistics.style';
import Bars from '../../layout/bars/bars.component';
import QuotaTypeNotes from '../quota-type-notes/quota-type-notes.component';
import { ThemeName } from '../../../types';
import Theme from '../../layout/theme/theme.component';

interface GlobalStatisticsProps {
    localLevel?: boolean
}

const idForGlobalOption: string = '0';

const GlobalStatistics: React.FC<GlobalStatisticsProps> = ({
    localLevel = false
}) => {
    const [{
        taxonomies
    }, {
        getCountryProfileResultList,
    }] = useAppState();

    const selectOptions = [
        {value: idForGlobalOption, label: 'Global statistics'},
        ...taxonomies.regions.map(region => ({
            value: String(region.id),
            label: 'Regional statistics: ' + region.name
        }))
    ];

    const [selectedOptionValue, changeSelectedOptionValue] = useState<string>('0');

    const selectedOption = (() => {
        for (const option of selectOptions) {
            if (option.value === selectedOptionValue) {
                return option;
            }
        }

        // this should never happen
        return selectOptions[0];
    })();

    const selectedRegion = selectedOptionValue === idForGlobalOption ? null : (() => {
        for (const region of taxonomies.regions) {
            if (region.id === Number(selectedOptionValue)) {
                return region;
            }
        }

        return null;
    })();

    const filteredCountryProfiles = toCountryProfileList(
        getCountryProfileResultList({
            regionIds: selectedRegion ? [selectedRegion.id] : [],
            datetime: null,
            localLevel,
        })
    );

    const ranges = {
        lessOrEqualThan19: {amount: 0, countryNames: Array<string>()},
        between20and29: {amount: 0, countryNames: Array<string>()},
        between30and39: {amount: 0, countryNames: Array<string>()},
        between40and49: {amount: 0, countryNames: Array<string>()},
        overOrEqualThan50: {amount: 0, countryNames: Array<string>()}
    };

    const electoralSystems: {[electoralSystemId: number]: number} = {};

    const legalSourcesData = {
        'Constitutions': 0,
        'Electoral laws': 0,
        'Local government laws': localLevel ? 0 : -1,
        'Parity laws': 0,
        'Party laws': 0,
        'Financing laws': 0,
        'Political agreement': 0
    };

    const theme: ThemeName = localLevel ? 'green' : 'blue';

    filteredCountryProfiles.forEach(countryProfile => {
        const {
            quotaTarget,
            countryId
        } = countryProfile;
        const countryName = getTaxonomyName(taxonomies, 'countries', countryId);

        if (quotaTarget >= 50) {
            ranges.overOrEqualThan50.amount++;
            ranges.overOrEqualThan50.countryNames.push(countryName);
        }
        else if (quotaTarget >= 40) {
            ranges.between40and49.amount++;
            ranges.between40and49.countryNames.push(countryName);
        }
        else if (quotaTarget >= 30) {
            ranges.between30and39.amount++;
            ranges.between30and39.countryNames.push(countryName);
        }
        else if (quotaTarget >= 20) {
            ranges.between20and29.amount++;
            ranges.between20and29.countryNames.push(countryName);
        }
        else {
            ranges.lessOrEqualThan19.amount++;
            ranges.lessOrEqualThan19.countryNames.push(countryName);
        }

        (Object.keys(ranges) as Array<keyof typeof ranges>).forEach(rangeKey => {
            ranges[rangeKey].countryNames.sort((a, b) => a > b ? 1 : -1);
        });

        if (electoralSystems[countryProfile.electoralSystemTypeId] === undefined) {
            electoralSystems[countryProfile.electoralSystemTypeId] = 0;
        }

        if (countryProfile.constitutionProvisionsGenderQuotaId === LEGAL_SOURCE_TRUE_ID) {
            legalSourcesData['Constitutions']++;
        }

        if (countryProfile.electoralProvisionsGenderQuotaId === LEGAL_SOURCE_TRUE_ID) {
            legalSourcesData['Electoral laws']++;
        }

        if (countryProfile.parityProvisionsGenderQuotaId === LEGAL_SOURCE_TRUE_ID) {
            legalSourcesData['Parity laws']++;
        }

        if (countryProfile.partyProvisionsGenderQuotaId === LEGAL_SOURCE_TRUE_ID) {
            legalSourcesData['Party laws']++;
        }

        if (countryProfile.financingProvisionsGenderQuotaId === LEGAL_SOURCE_TRUE_ID) {
            legalSourcesData['Financing laws']++;
        }

        if (countryProfile.politicalProvisionsGenderQuotaId === LEGAL_SOURCE_TRUE_ID) {
            legalSourcesData['Political agreement']++;
        }

        if (localLevel) {
            if (countryProfile.localProvisionsGenderQuotaId === LEGAL_SOURCE_TRUE_ID) {
                legalSourcesData['Local government laws']++;
            }
        }

        electoralSystems[countryProfile.electoralSystemTypeId]++;
    });

    const barsData: [number, string, string][] = [
        [ranges.overOrEqualThan50.amount, '50', ranges.overOrEqualThan50.countryNames.join(', ')],
        [ranges.between40and49.amount, '40 - 49', ranges.between40and49.countryNames.join(', ')],
        [ranges.between30and39.amount, '30 - 39', ranges.between30and39.countryNames.join(', ')],
        [ranges.between20and29.amount, '20 - 29', ranges.between20and29.countryNames.join(', ')],
        [ranges.lessOrEqualThan19.amount, '< 20', ranges.lessOrEqualThan19.countryNames.join(', ')],
    ];

    const electoralSystemItem = (
        amount: number,
        description: string,
        key: number = 0
    ) => (
        amount > -1 && <tr key={ key }>
            <td className={ globalStatisticsListNumberClassName }>
                { amount }
            </td>
            <td className={ globalStatisticsListDescriptionClassName }>
                { description }
            </td>
        </tr>
    );

    return (
        <Theme
            className={ globalStatisticsClassName }
            name={ theme }
            blueClassName={ globalStatisticsThemeBlueClassName }
            greenClassName={ globalStatisticsThemeGreenClassName }>
            <Content>
                <Grid>
                    <Cell size={ 12 } sizeTablet={ 'stack' }>
                        <Select
                            options={ selectOptions }
                            value={ selectedOptionValue }
                            onChange={ changeSelectedOptionValue }>
                                <div className={ globalStatisticsDropdownTitleClassName }>
                                    { selectedOption.label }
                                    <Icon name='dropdown' className={ globalStatisticsDropdownIconClassName }/>
                                </div>
                        </Select>
                    </Cell>
                    <Cell size={ 12 } sizeTablet={ 'fill' }>
                        <div className={ globalStatisticsSubtitleClassName }>
                            Select global or regional statistics
                        </div>
                    </Cell>
                </Grid>
                <Spacer x2/>
                <div className={ globalStatisticsGridWrapperNameClassName }>
                    <Grid>
                        <Cell size={ 12 } sizeTablet={ 6 } sizeDesktop={ 3 }>
                            <div className={ globalStatisticsDataNameClassName }>
                                Quota type
                            </div>
                            <QuotaTypeNotes
                                theme={ theme }
                                countryCount={ selectedRegion?.countryCount || COUNTRY_COUNT }/>
                            <div>
                                <QuotaTypeChart
                                    withCaption
                                    profiles={ !localLevel ? filteredCountryProfiles : undefined }
                                    profilesLocalLevel={ localLevel ? filteredCountryProfiles : undefined }
                                    countryCount={ selectedRegion?.countryCount || COUNTRY_COUNT }/>
                            </div>
                        </Cell>
                        <Cell size={ 12 } sizeTablet={ 6 } sizeDesktop={ 3 }>
                            <div className={ globalStatisticsDataNameClassName }>
                                Quota target range (% women)
                            </div>
                            <Bars
                                className={ globalStatisticsBarsClassName }
                                theme={ localLevel ? 'green' : 'blue' }
                                data={ barsData }/>
                            <div className={ globalStatisticsNoteClassName }>
                                Number of countries with legislated quotas
                            </div>
                        </Cell>
                        <Cell size={ 12 } sizeTablet={ 6 } sizeDesktop={ 3 }>
                            <div className={ globalStatisticsDataNameClassName }>
                                Electoral systems
                            </div>
                            <table className={ globalStatisticsListClassName }>
                                <tbody>
                                    { Object.keys(electoralSystems).map((electoralSystemId, index) => (
                                        electoralSystemItem(
                                            electoralSystems[Number(electoralSystemId)],
                                            getTaxonomyName(taxonomies, 'electoralSystemTypes', Number(electoralSystemId)),
                                            index
                                        )
                                    )) }
                                </tbody>
                            </table>
                            <div className={ globalStatisticsNoteClassName }>
                                Note: Number of countries with legislated quotas.
                            </div>
                        </Cell>
                        <Cell size={ 12 } sizeTablet={ 6 } sizeDesktop={ 3 }>
                            <div className={ globalStatisticsDataNameClassName }>
                                Legal sources
                            </div>
                            <table className={ globalStatisticsListClassName }>
                                <tbody>
                                    { (Object.keys(legalSourcesData) as (keyof typeof legalSourcesData)[]).map((legalSourceName, index) => (
                                        electoralSystemItem(
                                            legalSourcesData[legalSourceName],
                                            legalSourceName,
                                            index
                                        )
                                    )) }
                                </tbody>
                            </table>
                        </Cell>
                    </Grid>
                </div>
            </Content>
        </Theme>
    )
};

export default GlobalStatistics;
